import { useState, useEffect, useContext, useMemo } from "react"
import BwipJs from "@bwip-js/browser"
import { FileUploader } from "react-drag-drop-files"
import { enqueueSnackbar } from 'notistack'
import moment from 'moment-timezone'
import _ from "lodash"

import { Typography, Button, TextField } from "@mui/material"
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'

import { AppContext } from '/providers/app-context'
import {uploadClientImage, saveScannerConfiguration} from '/providers/templates-api'

import StyledPaper from '/components/paper'

const fileTypes = ["jpg", "jpeg", "png"]

const Stamp = ({ template, setTemplate }) => {

  const { apiSource, apiKey, client, setProcessing } = useContext(AppContext)

  const [barcode, setBarcode] = useState(null)
  const [barcodeSVG, setBarcodeSVG] = useState(null)
  const [newScannerData, setNewScannerData] = useState(_.clone(template.scanner_data))
  const [filename, setFilename] = useState()
  const [imageBase64, setImageBase64] = useState(null)

  const dataUnChanged = useMemo(() => JSON.stringify(template.scanner_data) === JSON.stringify(newScannerData),[template.scanner_data,newScannerData])

  const barcodeOptions = {
    bcid: 'qrcode',
    scale: 2,
    includetext: false,
    eclevel: "M",
    ecaddchars: 3,
    layers: 8,
    width: 60,
    height: 60,
    padding: 8,
    backgroundcolor: "#ffffff"
  }

  useEffect(() => {
    let canvas = document.createElement('canvas')

    BwipJs.toCanvas(canvas, { ...barcodeOptions, text: `https://signup.smartix.uk/loyalty/${template.client_id}/${template.template_id}` })

    let barcode = canvas.toDataURL('image/svg')

    setBarcode(barcode)

    // We also make an SVG version in case anyone wants to download it
    const barcodeSVG = BwipJs.toSVG({ ...barcodeOptions, text: `https://signup.smartix.uk/loyalty/${template.client_id}/${template.template_id}` })

    setBarcodeSVG(barcodeSVG)
  }, [])

  const updateScannerData = (field, value) => {
    let sd = _.clone(newScannerData)
    _.set(sd, field, value)
    setNewScannerData(sd)
  }

  const handleImageUpload = file => {
    setFilename(file.name)
    const blobUrl = URL.createObjectURL(file)
    updateScannerData("logo", blobUrl)

    const reader = new FileReader()
    reader.onloadend = () => {
      const b64 = reader.result.replace(/^data:image\/\w+;base64,/, '')
      setImageBase64(b64)
    }
    reader.readAsDataURL(file)
  }

  const save = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Saving configuration', variant: 'light' })

    let sd = _.clone(newScannerData)

    if (imageBase64) {
      const filename = await sendImageToS3()
      sd.logo = filename
    }

    setNewScannerData(sd)

    // Now save to DB
    await saveScannerConfiguration({ apiSource, apiKey, templateId: template.template_id, scannerData:sd })

    enqueueSnackbar({ message: 'Configuration saved', variant: 'success' })
    setProcessing(false)

    setImageBase64(null)

    // Now refresh the scanner data in parent module
    const t = _.cloneDeep(template)
    t.scanner_data = sd
    setTemplate(t)
  }

  const reset = () => {
    setNewScannerData(_.clone(template.scanner_data))
  }


  const sendImageToS3 = async () => {
    const fileData = filename.split(".")
    const suffix = fileData.pop()
    const prefix = fileData.join(".").replace(/[^A-Z0-9-]/ig, "-")

    const timestamp = moment().format("YYYYMMDDHHmmss")

    const uniqueFilename = `${prefix}-${timestamp}.${suffix}`

    // Now upload to S3
    const response = await uploadClientImage({ apiSource, apiKey, device:"scan", imageType:"logo",filename: uniqueFilename, base64Data:imageBase64 })
    //TODO handler error
    // And return full URL
    return response.s3_url

  }

  const downloadBarcodePng = () => {
    const img = new Image()
    img.src = barcode;
    img.onload = () => {
      // create Canvas
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      // create a tag
      const a = document.createElement('a')
      a.download = newScannerData.title.replace(/[ ]/g, "-") + "signup-barcode.png"
      a.href = canvas.toDataURL('image/png')
      document.body.appendChild(a) // Required for this to work in FireFox
      a.click()
    }
  }

  const downloadBarcodeSvg = () => {
    let svg = _.clone(barcodeSVG)
    //add name spaces.
    if (!svg.match(/^<svg[^>]+xmlns="http\:\/\/www\.w3\.org\/2000\/svg"/)) {
      svg = svg.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"')
    }
    if (!svg.match(/^<svg[^>]+"http\:\/\/www\.w3\.org\/1999\/xlink"/)) {
      svg = svg.replace(/^<svg/, '<svg xmlns:xlink="http://www.w3.org/1999/xlink"')
    }

    //add xml declaration
    svg = '<?xml version="1.0" standalone="no"?>\r\n' + svg

    const a = document.createElement('a')
    a.download = newScannerData.title.replace(/[ ]/g, "-") + "signup-barcode.svg"
    a.href = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg)
    document.body.appendChild(a) // Required for this to work in FireFox
    a.click()
  }

  if (template.pass_type_id === "smartix_loyalty") return (
    <>
      <Typography variant="h5">Loyalty card signup preview</Typography>
      <StyledPaper classes="mb-10">
        <div style={{ backgroundColor: "white", textAlign: "center" }} className="p-10">
          <Typography variant="h3" color="black">{template.scanner_data.retailer_name}</Typography>
          <Typography variant="h4" color="black">{template.scanner_data.title}</Typography>
          <img src={template.scanner_data.logo} style={{ margin: "auto", marginTop: 20, marginBottom: 20 }} />
          <Typography variant="h5" color="black">{template.scanner_data.description}</Typography>
        </div>
      </StyledPaper>

      <Typography variant="h5">Loyalty card signup form links and barcode images</Typography>
      <StyledPaper classes="mb-10">
        <div className="flex flex-row justify-between w-full p-5 gap-10">
          <div className="mb-5">
            <Typography variant="h5">
              Scan below or <a href={`https://signup.smartix.uk/loyalty/${template.client_id}/${template.template_id}`} target="_blank">Click here</a> to visit the signup form
            </Typography>
            <img src={barcode} className="mt-5" />
          </div>

          <div>
            <Typography variant="h5">Download signup barcode images</Typography>

            <div className="flex flex-col gap-5 mt-20">
              <Button onClick={downloadBarcodePng}><FileDownloadOutlinedIcon className="mr-5" />Download PNG Barcode</Button>
              <Button onClick={downloadBarcodeSvg}><FileDownloadOutlinedIcon className="mr-5" />Download SVG Barcode</Button>
            </div>
          </div>
        </div>
      </StyledPaper>

      <Typography variant="h5">Loyalty card signup configuration</Typography>
      <StyledPaper classes="mb-10">
        <div className="flex flex-col gap-5 w-full p-5">
          <TextField label="Retailer Name" value={newScannerData.retailer_name} onChange={e => updateScannerData("retailer_name", e.target.value)} />
          <TextField label="Promotion title" value={newScannerData.title} onChange={e => updateScannerData("title", e.target.value)} />
          <TextField label="Promotion description" value={newScannerData.description} onChange={e => updateScannerData("description", e.target.value)} />
          <TextField label="Number of free points to give a new collector" value={newScannerData.initial_points} onChange={e => updateScannerData("initial_points", e.target.value)} />
        </div>

        <div className="flex flex-col gap-5">
          <Typography variant="h6">Replace logo...</Typography>
          <FileUploader handleChange={handleImageUpload} types={fileTypes} label="Click to select image, or drag an image here (1200x400)" />
          <div style={{ backgroundColor: "white" }}><img src={newScannerData.logo} width="100%" /></div>
        </div>

        <div className="flex w-full justify-between">
          <Button onClick={reset} disabled={dataUnChanged}>Undo changes</Button>
          <Button onClick={save} disabled={dataUnChanged}>Save changes</Button>
        </div>

      </StyledPaper>

      <div className="p-5" />
    </>
  )
}

export default Stamp